import { useEffect, useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input, Select, Textarea } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { useCreateResourceLink, useResourceMeta, useUpdateResourceLink } from '../../hooks/useResources';
import type { ResourceLink } from '../../api/resources';

interface LinkEditorProps {
  isOpen: boolean;
  onClose: () => void;
  /** Null when adding. */
  link: ResourceLink | null;
  /** Pre-selects the section when adding from inside one. */
  defaultFeature?: string;
}

/**
 * Add or edit a link. Rendered for admins and members; the section picker only
 * lists the sections the API says this user may file under, and the API refuses
 * any other write regardless.
 */
export function LinkEditor({ isOpen, onClose, link, defaultFeature }: LinkEditorProps) {
  const meta = useResourceMeta(isOpen);
  const create = useCreateResourceLink();
  const update = useUpdateResourceLink();

  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [feature, setFeature] = useState('');
  const [category, setCategory] = useState('');
  const [source, setSource] = useState('other');
  const [tags, setTags] = useState('');
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTitle(link?.title ?? '');
    setUrl(link?.url ?? '');
    setDescription(link?.description ?? '');
    setFeature(link?.feature ?? defaultFeature ?? '');
    setCategory(link?.category ?? '');
    setSource(link?.source ?? 'other');
    setTags((link?.tags ?? []).join(', '));
    setIsPinned(link?.isPinned ?? false);
  }, [isOpen, link, defaultFeature]);

  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const submit = async () => {
    const payload = {
      title: title.trim(),
      url: url.trim(),
      description: description.trim() || null,
      feature,
      category: category.trim() || null,
      source,
      tags: tags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
      isPinned,
    };

    if (link) {
      // Editing is also the moment somebody confirms the link still resolves.
      await update.mutateAsync({ id: link.id, ...payload, verified: true });
    } else {
      await create.mutateAsync(payload);
    }
    onClose();
  };

  const canSubmit = title.trim() && url.trim() && feature && !pending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={link ? 'Edit link' : 'Add link'} size="lg">
      <div className="px-6 py-5 space-y-4">
        {error && <Alert variant="error">{error.message}</Alert>}

        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="MRR by month sheet"
          hint="What somebody would type when looking for it."
        />

        <Input
          label="URL"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://docs.google.com/spreadsheets/d/…"
        />

        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="One line on what it is and when to use it."
        />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Select
              id="link-feature"
              label="Section"
              value={feature}
              onChange={(e) => setFeature(e.target.value)}
              className="w-full"
            >
              <option value="">Choose a section…</option>
              {meta.data?.features.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </Select>
            <p className="mt-1.5 text-body-xs text-text-tertiary">
              Who sees this link. Only people who can open that area of the OS.
            </p>
          </div>

          <div>
            <Select
              id="link-source"
              label="Lives in"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full"
            >
              {(meta.data?.sources ?? ['other']).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Group"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="Decks"
            hint="Optional sub-heading within the section."
          />
          <Input
            label="Search terms"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="mrr, revenue, recurring"
            hint="Comma separated. Synonyms people actually type."
          />
        </div>

        <label className="flex items-center gap-2 text-body-sm text-text-secondary">
          <input
            type="checkbox"
            checked={isPinned}
            onChange={(e) => setIsPinned(e.target.checked)}
            className="rounded border-border"
          />
          Pin to the top of its section
        </label>
      </div>

      <div className="flex justify-end gap-2 border-t border-border-light px-6 py-4">
        <Button variant="ghost" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!canSubmit} isLoading={pending}>
          {link ? 'Save' : 'Add link'}
        </Button>
      </div>
    </Modal>
  );
}
